import { defineStore } from 'pinia'
import { computed, nextTick, ref } from 'vue'
import { runAnalysisWorkflow, runPriorArtWorkflow } from '@/api/virtualPatent.js'
import { logger } from '@moatmetrics/vue-logger'
import { getConceptPatentFilingYears, getEntitySummaryForConcepts } from '@/api/concepts.js'
import { useI18n } from 'vue-i18n'
import { generateDisclosureLandscape } from '@/services/clusterService.js'
import { useFlag } from '@unleash/proxy-client-vue'

export const useProspectivePatentPlaybookStore = defineStore('prospectivePatentPlaybook', () => {
    const { t } = useI18n()

    const defaultLoadingStates = {
        micsAiSummary: {
            loading: false,
            error: null,
        },
        concepts: {
            loading: false,
            error: null,
        },
        conceptPatentFilingYears: {
            loading: false,
            error: null,
        },
        competitorSummary: {
            loading: false,
            error: null,
        },
        priorArt: {
            loading: false,
            error: null,
        },
        landscape: {
            loading: false,
            error: null,
        },
    }

    const virtualPatentId = ref(null)
    const patentContext = ref(null)
    const analyzedContext = ref(null)
    const loadingStates = ref({ ...defaultLoadingStates })
    const aiSummary = ref(null)
    const aiDescription = ref(null)
    const concepts = ref(null)
    const conceptPatentFilingYears = ref(null)
    const priorArt = ref(null)
    const competitorSummaryData = ref([])
    const landscapeJobId = ref(null)

    const loading = computed(() => {
        return Object.values(loadingStates.value).some((state) => state.loading)
    })

    const error = computed(() => {
        return Object.values(loadingStates.value).some((state) => state.error !== null)
    })

    const contextIsDirty = computed(() => {
        if (analyzedContext.value === null) return false

        return analyzedContext.value !== patentContext.value
    })

    const conceptPks = computed(() => concepts.value?.map((c) => c.id) || [])
    const conceptNames = computed(() => concepts.value?.map((c) => c.name) || [])
    const techAreas = computed(() => aiSummary.value?.technologiesUsed?.map((t) => t.name) || [])
    const secondaryProductAreas = computed(
        () => aiSummary.value?.secondaryProductAreas?.map((t) => t.name) || []
    )

    const clusteringAllowed = useFlag('ipAlpha.landscapeBoard')

    const resetState = () => {
        patentContext.value = null
        analyzedContext.value = null
        loadingStates.value = { ...defaultLoadingStates }
        aiSummary.value = null
        aiDescription.value = null
        concepts.value = null
        conceptPatentFilingYears.value = null
        priorArt.value = null
        competitorSummaryData.value = []
    }

    const setAllLoadingStates = (loading, error = null) => {
        Object.keys(loadingStates.value).forEach((key) => {
            loadingStates.value[key].loading = loading
            loadingStates.value[key].error = error
        })
    }

    const setLoadingState = (stateKey, loading, error = null) => {
        if (loadingStates.value[stateKey]) {
            loadingStates.value[stateKey].loading = loading
            loadingStates.value[stateKey].error = error
        }
    }

    const setVirtualPatentId = (id) => {
        virtualPatentId.value = id
    }

    const runAllAnalytics = async () => {
        analyzedContext.value = patentContext.value
        setAllLoadingStates(true)
        aiSummary.value = null
        concepts.value = null
        conceptPatentFilingYears.value = null
        competitorSummaryData.value = null

        await Promise.all([runProductAnalysis()])
    }

    const runProductAnalysis = async () => {
        loadingStates.value.micsAiSummary.loading = true
        loadingStates.value.micsAiSummary.error = null
        loadingStates.value.concepts.loading = true
        loadingStates.value.concepts.error = null
        loadingStates.value.conceptPatentFilingYears.loading = true
        loadingStates.value.conceptPatentFilingYears.error = null
        loadingStates.value.competitorSummary.loading = true
        loadingStates.value.competitorSummary.error = null
        loadingStates.value.priorArt.loading = true
        loadingStates.value.priorArt.error = null
        loadingStates.value.landscape.loading = true
        loadingStates.value.landscape.error = null
        aiSummary.value = null
        aiDescription.value = null
        concepts.value = null
        conceptPatentFilingYears.value = null
        competitorSummaryData.value = null
        landscapeJobId.value = null

        await getAiSummary()

        await nextTick()

        // synchronous call because concept pks are returned in aiSummary
        await Promise.all([
            getCompetitorSummary(),
            loadConceptPatentFilingYears(),
            runPriorArt(),
            generateLandscape(),
        ])
    }

    const getAiSummary = async () => {
        loadingStates.value.micsAiSummary.loading = true
        loadingStates.value.micsAiSummary.error = null
        loadingStates.value.concepts.loading = true
        loadingStates.value.concepts.error = null
        aiSummary.value = null
        aiDescription.value = null
        concepts.value = null

        try {
            const analysisResult = await runAnalysisWorkflow(patentContext.value)

            aiSummary.value = analysisResult.patentSummary
            aiDescription.value = analysisResult.patentDescription
            concepts.value = analysisResult.patentSummary?.primaryProductAreas

            loadingStates.value.micsAiSummary.loading = false
            loadingStates.value.concepts.loading = false
        } catch (err) {
            setAllLoadingStates(false)
            throw err
        }
    }

    const loadConceptPatentFilingYears = async () => {
        loadingStates.value.conceptPatentFilingYears.loading = true
        loadingStates.value.conceptPatentFilingYears.error = null
        conceptPatentFilingYears.value = null

        try {
            if (!concepts.value || concepts.value.length < 1) {
                loadingStates.value.conceptPatentFilingYears.loading = false
                loadingStates.value.conceptPatentFilingYears.error = t(
                    'prospectivePatentAnalysis.errors.noMappedProducts'
                )

                return
            }

            const conceptPatentsResponse = await getConceptPatentFilingYears(conceptPks.value, 20)

            if (conceptPatentsResponse.data?.length === 0) {
                loadingStates.value.conceptPatentFilingYears.error = t(
                    'prospectivePatentAnalysis.errors.noMappedProducts'
                )
                return
            }

            conceptPatentFilingYears.value = conceptPatentsResponse.data
        } catch (err) {
            logger.error(err)
            loadingStates.value.conceptPatentFilingYears.error = err.message
        } finally {
            loadingStates.value.conceptPatentFilingYears.loading = false
        }
    }

    const getCompetitorSummary = async () => {
        loadingStates.value.competitorSummary.loading = true
        loadingStates.value.competitorSummary.error = null
        competitorSummaryData.value = null

        try {
            if (!concepts.value || concepts.value.length < 1) {
                loadingStates.value.competitorSummary.loading = false
                loadingStates.value.competitorSummary.error = t(
                    'prospectivePatentAnalysis.errors.noMappedProducts'
                )
                return
            }

            const compSummaryResponse = await getEntitySummaryForConcepts(conceptPks.value)

            competitorSummaryData.value = compSummaryResponse.data
        } catch (err) {
            logger.error(err)
            loadingStates.value.competitorSummary.error = err.message
        } finally {
            loadingStates.value.competitorSummary.loading = false
        }
    }

    const runPriorArt = async () => {
        loadingStates.value.priorArt.loading = true
        loadingStates.value.priorArt.error = null
        priorArt.value = null
        try {
            const priorArtResult = await runPriorArtWorkflow(patentContext.value)
            priorArt.value = priorArtResult.priorArt
        } catch (err) {
            logger.error(err)
            loadingStates.value.priorArt.error = err.message
        } finally {
            loadingStates.value.priorArt.loading = false
        }
    }

    const generateLandscape = async () => {
        if (!clusteringAllowed.value) {
            loadingStates.value.landscape.loading = false
            loadingStates.value.priorArt.error = null
            return
        }

        loadingStates.value.landscape.loading = true
        loadingStates.value.priorArt.error = null
        landscapeJobId.value = null

        try {
            landscapeJobId.value = await generateDisclosureLandscape(
                virtualPatentId.value,
                aiDescription.value,
                conceptNames.value,
                [...conceptNames.value, ...techAreas.value],
                Math.max(4, conceptNames.value.length + secondaryProductAreas.value.length)
            )
        } catch (err) {
            logger.error(err)
            loadingStates.value.landscape.error = err.message
        } finally {
            loadingStates.value.landscape.loading = false
        }
    }

    return {
        virtualPatentId,
        patentContext,
        analyzedContext,
        aiSummary,
        aiDescription,
        concepts,
        conceptNames,
        conceptPks,
        landscapeJobId,
        conceptPatentFilingYears,
        competitorSummaryData,
        loadingStates,
        loading,
        error,
        contextIsDirty,
        techAreas,
        secondaryProductAreas,
        runAllAnalytics,
        priorArt,
        setLoadingState,
        setVirtualPatentId,
        resetState,
    }
})
